import React, { useCallback } from 'react';

import { ActionIcon, createStyles, Group, Paper, Text, Title, Tooltip } from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { useMediaQuery } from '@mantine/hooks';
import { IconFilterOff } from '@tabler/icons-react';
import BackButton from 'components/BackButton';
import DownloadCSV from 'components/DownloadCSV';
import EventTypeMultiSelectAutoComplete, {
  IEventTypeMultiSelectAutoComplete,
} from 'components/form-fields/auto-complete-fields/EventTypeMultiSelectAutoComplete';
import AthleteStatusMultiSelect from 'components/form-fields/select-fields/AthleteStatusMultiSelect';
import HelpButton from 'components/HelpButton';
import dayjs from 'dayjs';
import useAthleteAttendance from 'hooks/reports/athletes/useAthleteAttendance';
import { useNavigate } from 'react-router-dom';
import { AthleteAttendanceReport } from 'types';

import { SearchFilters } from '../types';

const useStyles = createStyles(theme => ({
  header: {
    width: '100%',
    background: theme.colors.gray[0],
    padding: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
    display: 'grid',
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: 0,
    gridTemplateColumns: 'min-content auto 1fr 1fr 1fr min-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `"backButton title events dateRange statuses clear download"`,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('lg')]: {
      gridTemplateColumns: 'min-content auto auto min-content min-content',
      gridTemplateAreas: `
        'backButton title title clear download'
        'dateRange dateRange statuses statuses statuses'
        'events events events events events'
        `,
    },
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: 'min-content auto min-content',
      gridTemplateAreas: `
        'backButton title clear'
        'dateRange dateRange dateRange'
        'statuses statuses statuses'
        'events events events'
        `,
    },
  },
  backButton: {
    gridArea: 'backButton',
  },
  title: {
    gridArea: 'title',
    whiteSpace: 'nowrap',
  },
  events: {
    gridArea: 'events',
  },
  dateRange: {
    gridArea: 'dateRange',
    minWidth: '15rem',
  },
  statuses: {
    gridArea: 'statuses',
  },
  clear: {
    gridArea: 'clear',
  },
  download: {
    gridArea: 'download',
    [theme.fn.smallerThan('sm')]: {
      display: 'none',
    },
  },
}));

const csvOptions = {
  fieldSeparator: ',',
  quoteStrings: '',
  decimalSeparator: '.',
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: true,
};

const dataToRow = (d: AthleteAttendanceReport): { [key: string]: any } => ({
  'Full Name': d.fullName,
  'Current Status': d.athleteStatus,
  Email: d.email,
  Phone: d.phone,
  'Class Attendance': d.checkedInClasses,
  'Class Reservations': d.reservedClasses,
  'Appointment Attendance': d.checkedInAppointments,
  'Appointment Reservations': d.reservedAppointments,
  'Imported Event Attendance': d.checkedInImportedEvents,
  'Imported Event Reservations': d.reservedImportedEvents,
  'Total Attendance': d.checkedInEvents,
  'Total Reservations': d.reservedEvents,
  'Current Packages': d.currentPackages?.length
    ? d.currentPackages.map(p => (p.state === 'HOLD' ? `${p.name} (on hold)` : p.name)).join('; ')
    : '',
});

const AthleteAttendancePageHeader = ({
  initialSearchFiltersValue,
  searchFilters,
  setSearchFilters,
  orderingValue,
}: {
  initialSearchFiltersValue: SearchFilters;
  searchFilters: SearchFilters;
  setSearchFilters: React.Dispatch<React.SetStateAction<SearchFilters>>;
  orderingValue: string;
}): JSX.Element => {
  const { classes, theme } = useStyles();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const navigate = useNavigate();

  const handleEventsChange = (values: IEventTypeMultiSelectAutoComplete[]): void => {
    setSearchFilters(prev => ({ ...prev, eventTypes: values }));
  };
  const handleBackClick = useCallback((): void => {
    navigate(-1);
  }, [navigate]);

  const handleDateChange = (value: DatesRangeValue): void => {
    setSearchFilters(prev => ({ ...prev, dateRange: value }));
  };

  const handleStatusChange = (value: string[]): void => {
    setSearchFilters(prev => ({ ...prev, athleteStatuses: value }));
  };

  const handleClearFilters = (): void => {
    setSearchFilters(initialSearchFiltersValue);
  };

  return (
    <Paper className={classes.header}>
      <BackButton handleOnClick={handleBackClick} />
      <Group noWrap spacing='xl' className={classes.title}>
        <Title order={isDesktop ? 1 : 3}>Athlete Attendance</Title>
        <HelpButton width={400} position={isDesktop ? 'right' : 'bottom'}>
          <Text size='sm' style={{ wordBreak: 'break-word', whiteSpace: 'break-spaces' }}>
            A report of event attendance by athlete across the selected date range. Attendance is defined as
            a check-in. Reservations are calculated by the date of the event rather than the date the
            reservation was made.
            <br />
            <br /> Athletes who were on hold at any time during the selected range will be grayed out and
            marked with an on-hold indicator, allowing you to distinguish intentional absences from potential
            at-risk members.
            <br />
            <br /> Note: If you have imported attendance data from a past provider, totals may not equal the
            sum of classes and appointments.
          </Text>
        </HelpButton>
      </Group>
      <EventTypeMultiSelectAutoComplete
        className={classes.events}
        handleChange={handleEventsChange}
        values={searchFilters.eventTypes}
      />
      <DatePickerInput
        className={classes.dateRange}
        value={searchFilters.dateRange}
        onChange={handleDateChange}
        valueFormat='MMMM DD, YYYY'
        type='range'
        allowSingleDateInRange
        required
        clearable
        {...{ placeholder: 'Select date range' }}
      />
      <AthleteStatusMultiSelect
        className={classes.statuses}
        placeholder='Select statuses'
        onChange={handleStatusChange}
        value={searchFilters.athleteStatuses}
        clearable
      />
      <Tooltip label='Clear filters' className={classes.clear}>
        <ActionIcon onClick={handleClearFilters}>
          <IconFilterOff />
        </ActionIcon>
      </Tooltip>
      <DownloadCSV
        useList={useAthleteAttendance}
        dataToRow={dataToRow}
        csvOptions={csvOptions}
        hookOptions={{
          events: !!searchFilters.eventTypes.length ? searchFilters.eventTypes.map(e => e.eventTypeId) : [],
          dateStart: searchFilters.dateRange[0]
            ? dayjs(searchFilters.dateRange[0]).format('YYYY-MM-DD')
            : undefined,
          dateEnd: searchFilters.dateRange[1]
            ? dayjs(searchFilters.dateRange[1]).format('YYYY-MM-DD')
            : undefined,
          athleteStatuses: searchFilters.athleteStatuses,
          ordering: orderingValue,
        }}
        className={classes.download}
        reportType='athlete-attendance'
        startDate={
          searchFilters.dateRange[0] ? dayjs(searchFilters.dateRange[0]).format('YYYY-MM-DD') : null
        }
        endDate={searchFilters.dateRange[1] ? dayjs(searchFilters.dateRange[1]).format('YYYY-MM-DD') : null}
      />
    </Paper>
  );
};

export default AthleteAttendancePageHeader;
